import { useState, useRef, useCallback, useMemo } from 'react'
import { FormControl } from '@mui/material'
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react'

import { Tooltip } from '@components/shared/Tooltip'
import { Loading } from '@components/shared/Select/components/Loading'
import { normalizeAllExclusive } from '../../utils/normalizeAllExclusive'

import type { ComponentProps } from './types'

import * as S from './styles'

export const Component = ({
  name,
  label,
  placeholder,
  required,
  options,
  error,
  loading,
  tooltip,
  value = [],
  onChange: onChangeProp,
  ...rest
}: ComponentProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const selectWrapperRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<'left' | 'right'>('left')

  const isDisabled = Boolean(rest.disabled)

  const selectedArray = useMemo(() => {
    return Array.isArray(value) ? value : (value ? [value] : [])
  }, [value])

  const handleChange = useCallback(
    (event: { target: { value: unknown } }) => {
      const raw = event.target.value as string | number | (string | number)[]
      const normalized = normalizeAllExclusive(raw, options, selectedArray)
      onChangeProp?.(normalized as any, undefined as any)
    },
    [options, selectedArray, onChangeProp]
  )

  const selectedLabels = useMemo(() => {
    return selectedArray
      .map((val) => options.find((opt) => opt.value === val)?.label)
      .filter(Boolean)
  }, [selectedArray, options])

  const handleOpen = useCallback(() => {
    if (selectWrapperRef.current) {
      const rect = selectWrapperRef.current.getBoundingClientRect()
      const windowWidth = window.innerWidth
      const spaceOnRight = windowWidth - rect.right
      const minSpace = 250

      if (spaceOnRight < minSpace) {
        setMenuAnchor('right')
      } else {
        setMenuAnchor('left')
      }
    }
    setOpen(true)
  }, [])

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isDisabled) return
    const selectDiv = selectWrapperRef.current?.querySelector<HTMLElement>('.MuiSelect-select')
    selectDiv?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
  }

  return (
    <FormControl disabled={isDisabled} fullWidth>
      <S.Label disabled={isDisabled}>
        {label}
        {required && ' *'}
      </S.Label>

      <S.SelectWrapper ref={selectWrapperRef}>
        <S.Select
          inputRef={inputRef}
          multiple
          name={name}
          error={!!error}
          onOpen={handleOpen}
          onClose={() => setOpen(false)}
          displayEmpty
          value={selectedArray}
          IconComponent={() => {
            return (
              <>
                {loading ? (
                  <Loading hasText={false} size="small" />
                ) : (
                  <S.IconBox error={!!error} isDisabled={isDisabled}>
                    {open ? (
                      <CaretUpIcon weight="bold" onClick={!isDisabled ? handleIconClick : undefined} />
                    ) : (
                      <CaretDownIcon weight="bold" onClick={!isDisabled ? handleIconClick : undefined} />
                    )}
                  </S.IconBox>
                )}
              </>
            )
          }}
          MenuProps={{
            PaperProps: { component: S.MenuPaper },
            MenuListProps: { component: S.MenuList },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: menuAnchor
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: menuAnchor
            }
          }}
          renderValue={(selected) => {
            if (!selected || (Array.isArray(selected) && selected.length === 0)) return <S.Placeholder>{placeholder}</S.Placeholder>

            const displayText = selectedLabels.join(', ')

            return (
              <Tooltip title={tooltip || ''} placement="top">
                <S.SelectedValue>{displayText}</S.SelectedValue>
              </Tooltip>
            )
          }}
          disabled={isDisabled || loading}
          {...rest}
          onChange={handleChange}
        >
          {options.map((item) => (
            <S.MenuItem key={item.label} value={item.value} disabled={item.disabled}>
              {item.label}
            </S.MenuItem>
          ))}
        </S.Select>
      </S.SelectWrapper>

      {typeof error === 'string' && <S.ErrorMessage>{error}</S.ErrorMessage>}
    </FormControl>
  )
}
